import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlockMath, InlineMath } from 'react-katex';
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SectionWrapper from '@/components/SectionWrapper';
import FormulaCard from '@/components/FormulaCard';
import SequenceAnimator from '@/calculators/SequenceAnimator';
import AOperatorExplorer from '@/calculators/AOperatorExplorer';
import SequenceDecomposerCalc from '@/calculators/SequenceDecomposerCalc';
import SequencePhasorViz from '@/calculators/SequencePhasorViz';

// ─── Color-coding legend (reused in multiple sections) ────────────────────────
const SequenceLegend = () => (
  <div
    data-testid="sequence-legend"
    className="flex items-center gap-5 flex-wrap py-2 px-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono"
  >
    {[
      { color: '#0d9488', label: 'Positive (V\u2081)', sub: 'ABC rotation' },
      { color: '#ea580c', label: 'Negative (V\u2082)', sub: 'ACB rotation' },
      { color: '#7c3aed', label: 'Zero (V\u2080)',     sub: 'Cophasal' },
    ].map(({ color, label, sub }) => (
      <span key={label} className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }} />
        <span className="font-semibold" style={{ color }}>{label}</span>
        <span className="text-slate-400 dark:text-slate-500">— {sub}</span>
      </span>
    ))}
  </div>
);

// ─── Section 1: Why It Matters ────────────────────────────────────────────────
const WhyItMatters = () => (
  <SectionWrapper number="1" title="Why It Matters" id="why-it-matters">
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-5 mb-5">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">Real Utility Scenario</p>
      <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm">
        A 13.8 kV distribution feeder has a bolted single-line-to-ground (SLG) fault on Phase A.
        The relay engineer uses balanced positive-sequence analysis to predict the fault current:{' '}
        <InlineMath math="I_F = V_F / Z_1 = 1.0/j0.15 = 6.67\,\text{pu}" />. But field measurements
        show only 2.22 pu. The discrepancy exists because an SLG fault drives current through
        <em> three</em> sequence impedances in series:{' '}
        <InlineMath math="Z_1 + Z_2 + Z_0" />. For a typical overhead feeder,{' '}
        <InlineMath math="Z_0 \approx 3Z_1" />, so the actual fault current is{' '}
        <InlineMath math="I_F = 3V_F/(Z_1+Z_2+Z_0) = 3/(j0.45) = 6.67\,\text{pu}" /> — wait, that is
        higher, not lower. The engineer then checks the substation grounding: the transformer is
        delta-connected on the HV side, making <InlineMath math="Z_0 = \infty" />. No ground fault
        current can flow at all — the relay will never trip. Without symmetrical components, this
        grounding blindspot is invisible.
      </p>
    </div>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
      In 1918, Charles Fortescue proved that{' '}
      <strong className="text-slate-800 dark:text-slate-200">
        any unbalanced three-phase set can be decomposed into exactly three balanced sets
      </strong>{' '}
      — each rotating at the same frequency but in different phase sequences. This mathematical
      breakthrough made unbalanced fault analysis tractable for the first time and remains the
      foundation of all ground-fault protection, negative-sequence relay design, and voltage-unbalance
      assessment in distribution systems.
    </p>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
      Every SLG fault current calculation, every differential relay setting, every 51G/51Q relay pickup
      value, and every power quality voltage-unbalance study uses symmetrical components — even when the
      engineer's software hides the algebra. Understanding the decomposition is essential for checking
      those results, debugging unexpected relay operations, and correctly modeling transformer
      zero-sequence behavior.
    </p>
  </SectionWrapper>
);

// ─── Section 2: Concept Animator ─────────────────────────────────────────────
const ConceptAnimator = () => (
  <SectionWrapper number="2" title="Concept Animator" id="concept-visual" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      Drag any phasor tip in the diagram below to create an unbalanced three-phase set. The three
      sequence components update in real time, giving you a live visual of Fortescue's decomposition
      in action. Press <strong className="text-slate-800 dark:text-slate-200">Simulate SLG Fault</strong>{' '}
      to see the equal-magnitude signature.
    </p>
    <SequenceLegend />
    <div className="mt-4">
      <SequenceAnimator />
    </div>
  </SectionWrapper>
);

// ─── Section 3: Core Theory ───────────────────────────────────────────────────
const THEORY_ITEMS = [
  {
    id: 't1',
    title: "Fortescue's Theorem — The Core Insight",
    body: "Charles Fortescue (1918) proved that any set of n unbalanced phasors can be expressed as the sum of n balanced (or symmetrical) sets of phasors. For a three-phase system, the three symmetrical sets are the positive-sequence set, the negative-sequence set, and the zero-sequence set. The decomposition is unique — there is exactly one way to write any unbalanced three-phase set as this sum. This is not an approximation; it is an exact mathematical identity, valid for any arbitrary unbalanced phasors including severely faulted conditions.",
  },
  {
    id: 't2',
    title: 'The Three Sequence Types — Physical Meanings',
    body: "Positive-sequence (teal, V\u2081): Three equal-magnitude phasors, 120\u00b0 apart, rotating ABC counterclockwise. This is the normal balanced system. Motors produce torque and generators produce real power in the positive direction. V\u2081 \u2248 1.0 pu in steady-state operation.\n\nNegative-sequence (orange, V\u2082): Three equal-magnitude phasors, 120\u00b0 apart, rotating ACB (reverse sequence). This creates a reverse-rotating magnetic field in motors and generators, producing no useful work but inducing eddy-current heating in the rotor at twice supply frequency. Grid codes (NERC, IEC 61000-3-13) limit V\u2082/V\u2081 to 1\u20132%.\n\nZero-sequence (purple, V\u2080): Three phasors of equal magnitude that are all in phase — they point in the same direction and rotate together. Zero-sequence current flows equally in all three phases in the same direction simultaneously, and must return through a neutral wire or earth. It cannot flow in a circuit without a ground return path.",
  },
  {
    id: 't3',
    title: 'The a-Operator — Mathematical Engine',
    body: "The a-operator a = 1\u2220120\u00b0 = e^{j2\u03c0/3} \u2248 \u22120.5 + j0.866 is a unit phasor (|a| = 1) that rotates any complex number exactly 120\u00b0 counterclockwise. Applying it three times returns the original phasor: a\u00b3 = 1\u2220360\u00b0 = 1.\n\nKey identities:\n\u2022 a = 1\u2220120\u00b0  |  a\u00b2 = 1\u2220240\u00b0  |  a\u00b3 = 1\u22200\u00b0 = 1\n\u2022 1 + a + a\u00b2 = 0  (three equally-spaced unit phasors always sum to zero)\n\u2022 a = \u22120.5 + j0.8660  |  a\u00b2 = \u22120.5 \u2212 j0.8660\n\nThe transformation matrix is built entirely from powers of a. In the forward transform, multiplying Vb by a before summing tests how much of Vb\u2019s contribution aligns with the positive-sequence direction. The identity 1 + a + a\u00b2 = 0 is why a perfectly balanced set gives V\u2080 = V\u2082 = 0 exactly.",
  },
  {
    id: 't4',
    title: 'Zero-Sequence and Ground Paths — Critical for Protection',
    body: "Zero-sequence current flows in all three phases simultaneously and in the same direction, so it must return through a fourth conductor: the neutral wire or earth. This has critical implications:\n\n\u2022 Delta-connected winding: zero-sequence current cannot flow in the external line conductors. The delta winding presents an open circuit in the zero-sequence network on the line-conductor side.\n\u2022 Wye-connected winding with solidly grounded neutral: zero-sequence current flows through the neutral. The winding appears as an impedance (or short circuit) in the zero-sequence network.\n\u2022 Ungrounded wye or wye with high-resistance grounding: appears as a very high impedance (nearly open) in the zero-sequence network.\n\nThis is why transformer connection type (Dyn11, YNyn, YNd, etc.) directly determines whether SLG fault current can flow \u2014 and therefore determines the zero-sequence network topology used in every fault study.",
  },
];

const CoreTheory = () => (
  <SectionWrapper number="3" title="Core Theory" id="core-theory">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      The theory behind symmetrical components rests on three pillars: Fortescue's decomposition theorem,
      the three sequence types and their physical meanings, and the a-operator that encodes the 120° rotation.
      Master these and the transformation formulas become transparent.
    </p>
    <Accordion type="single" collapsible defaultValue="t1" className="space-y-2" data-testid="theory-accordion">
      {THEORY_ITEMS.map(item => (
        <AccordionItem
          key={item.id}
          value={item.id}
          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 bg-white dark:bg-slate-900"
        >
          <AccordionTrigger className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:no-underline hover:text-teal-700 dark:hover:text-teal-400 py-3">
            <span className="flex items-center gap-2.5 text-left">
              <span className="text-teal-600 dark:text-teal-500 font-mono font-bold text-xs w-4 flex-shrink-0">
                {item.id.replace('t', '')}
              </span>
              {item.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pb-4 space-y-3">
              {item.body.split('\n\n').map((para, pi) => (
                <p key={pi} className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">{para}</p>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </SectionWrapper>
);

// ─── Section 4: Formula Sheet ─────────────────────────────────────────────────
const FormulaSheet = () => (
  <SectionWrapper number="4" title="Formula Sheet" id="formulas" alt>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormulaCard
        title="Forward Transformation — Decomposition (IEEE Std 1110)"
        formula={"\\begin{aligned} V_0 &= \\tfrac{1}{3}(V_a + V_b + V_c) \\\\ V_1 &= \\tfrac{1}{3}(V_a + aV_b + a^2 V_c) \\\\ V_2 &= \\tfrac{1}{3}(V_a + a^2 V_b + a V_c) \\end{aligned}"}
        variables={[
          { sym: 'V\u2080', desc: 'Zero-sequence voltage (pu) \u2014 cophasal component, zero in balanced systems' },
          { sym: 'V\u2081', desc: 'Positive-sequence voltage (pu) \u2014 ABC rotation, equals rated voltage when balanced' },
          { sym: 'V\u2082', desc: 'Negative-sequence voltage (pu) \u2014 ACB rotation, zero in balanced systems' },
          { sym: 'Va, Vb, Vc', desc: 'Phase voltages (pu), phasor form. Va is the conventional reference \u22200\u00b0.' },
          { sym: 'a', desc: '1\u2220120\u00b0 = \u22120.5 + j0.8660. Unit rotation operator, |a| = 1.' },
        ]}
        note="Assumption: voltages are phasors (complex numbers). IEEE convention Va = reference \u22200\u00b0. Numerical example: balanced set Va=1\u22200\u00b0, Vb=1\u2220\u2212120\u00b0, Vc=1\u2220120\u00b0 \u2192 V\u2080 = 0, V\u2081 = 1\u22200\u00b0 pu, V\u2082 = 0. Only V\u2081 remains."
      />
      <FormulaCard
        title="Inverse Transformation — Reconstruction"
        formula={"\\begin{aligned} V_a &= V_0 + V_1 + V_2 \\\\ V_b &= V_0 + a^2 V_1 + a V_2 \\\\ V_c &= V_0 + a V_1 + a^2 V_2 \\end{aligned}"}
        variables={[
          { sym: 'Va', desc: 'Phase A voltage = V\u2080 + V\u2081 + V\u2082 (direct sum, no operator needed for phase A)' },
          { sym: 'Vb', desc: 'Phase B voltage: V\u2080 + a\u00b2V\u2081 + aV\u2082 (phase B receives a\u00b2 of positive, a of negative)' },
          { sym: 'Vc', desc: 'Phase C voltage: V\u2080 + aV\u2081 + a\u00b2V\u2082 (phase C receives a of positive, a\u00b2 of negative)' },
        ]}
        note="Numerical example: V\u2080=1/3, V\u2081=1/3, V\u2082=1/3 (all \u22200\u00b0) \u2192 Va = 1\u22200\u00b0; Vb = (1/3)(1+a\u00b2+a) = 0; Vc = (1/3)(1+a+a\u00b2) = 0 \u2713 SLG fault pattern verified."
      />
      <FormulaCard
        title="The a-Operator (IEEE Fortescue Convention)"
        formula={"a = 1\\angle 120^\\circ = e^{j2\\pi/3} = -\\frac{1}{2} + j\\frac{\\sqrt{3}}{2} \\approx -0.5 + j0.8660"}
        variables={[
          { sym: 'a',      desc: '1\u2220120\u00b0. Unit rotation operator. Multiplying any phasor by a rotates it 120\u00b0 CCW.' },
          { sym: 'a\u00b2', desc: '1\u2220240\u00b0 = \u22120.5 \u2212 j0.8660. Rotates 240\u00b0 CCW (equivalent to 120\u00b0 CW).' },
          { sym: 'a\u00b3', desc: '1\u2220360\u00b0 = 1. Applying a three times returns the original phasor exactly.' },
          { sym: '1+a+a\u00b2', desc: '= 0. Three unit phasors at 0\u00b0/120\u00b0/240\u00b0 always sum to zero \u2014 the balanced condition.' },
        ]}
        note="Critical identity: 1 + a + a\u00b2 = 0. Verify: 1 + (\u22120.5+j0.866) + (\u22120.5\u2212j0.866) = 0 \u2713. This identity is why V\u2080 = V\u2082 = 0 for any balanced set \u2014 the Vb and Vc terms cancel exactly in the forward transform rows."
      />
      <FormulaCard
        title="Transformation Matrix Form (Compact)"
        formula={"\\mathbf{V}_{012} = \\tfrac{1}{3}\\mathbf{T}^{-1}\\mathbf{V}_{abc} \\;\\Leftrightarrow\\; \\mathbf{V}_{abc} = \\mathbf{T}\\,\\mathbf{V}_{012}"}
        variables={[
          { sym: 'V\u2080\u2081\u2082', desc: 'Column vector [V\u2080, V\u2081, V\u2082]\u1d40 \u2014 sequence-domain voltages (pu)' },
          { sym: 'Vabc',   desc: 'Column vector [Va, Vb, Vc]\u1d40 \u2014 phase-domain voltages (pu)' },
          { sym: 'T',      desc: '[1 1 1; 1 a\u00b2 a; 1 a a\u00b2] \u2014 Fortescue\u2019s transformation matrix' },
          { sym: 'T\u207b\u00b9', desc: '[1 1 1; 1 a a\u00b2; 1 a\u00b2 a] \u2014 inverse transformation matrix' },
        ]}
        note="Applies to voltages AND currents: I\u2080\u2081\u2082 = (1/3)T\u207b\u00b9 Iabc. Sequence impedances Z\u2081, Z\u2082, Z\u2080 are defined in the same rotated complex domain. Same MVA and kV base used for all three sequence networks."
      />
    </div>
  </SectionWrapper>
);

// ─── Section 5: Common Mistakes ────────────────────────────────────────────────
const MISTAKES = [
  {
    title: "Swapping a and a\u00b2 in the transformation formulas",
    body: "The forward transform uses a in the V\u2081 row and a\u00b2 in the V\u2082 row \u2014 NOT the other way around. Specifically: V\u2081 = (1/3)(Va + a\u00b7Vb + a\u00b2\u00b7Vc) and V\u2082 = (1/3)(Va + a\u00b2\u00b7Vb + a\u00b7Vc). Swapping them silently exchanges V\u2081 and V\u2082, producing wrong sequence magnitudes and incorrect fault current calculations. Memory aid: the positive-sequence row (V\u2081) uses a (not a\u00b2) on Vb, because multiplying Vb by 1\u2220120\u00b0 rotates it forward to align with the positive-sequence ABC rotation direction.",
  },
  {
    title: "Forgetting the 1/3 scale factor in the forward transform",
    body: "The forward (decomposition) transform has a 1/3 pre-multiplier; the inverse (reconstruction) does not. The transformation matrix T is not unitary \u2014 T\u207b\u00b9 \u2260 T\u1d40. A missing 1/3 makes each sequence component 3\u00d7 too large. Quick verification: apply the forward transform to a balanced set. You should get V\u2081 = 1.0 pu. If you get V\u2081 = 3.0 pu, you forgot the factor of 1/3.",
  },
  {
    title: "Assuming zero-sequence current can flow in any circuit",
    body: "Zero-sequence current requires a physical ground return path. In delta-connected windings, there is no neutral or ground connection in the line conductors, so zero-sequence current cannot flow in the external lines. The delta winding appears as an open circuit in the zero-sequence network. This is not an approximation \u2014 it is exact. A transformer with a delta-connected HV winding will show zero SLG fault current contribution from the HV source, regardless of how large the source is.",
  },
  {
    title: "Treating negative-sequence as only a power quality metric",
    body: "Negative-sequence voltage V\u2082 is simultaneously a power quality metric AND a protection relay input. The 46 (negative-sequence overcurrent) relay uses I\u2082 to detect single-phasing and unbalanced loading. The 47 (negative-sequence voltage) relay uses V\u2082. Meanwhile, I\u2082 in motors induces rotor eddy-current heating at 120 Hz, which accelerates insulation degradation. Engineers who set only positive-sequence overcurrent relays without negative-sequence elements miss phase-to-phase faults that produce minimal I\u2081 but large I\u2082.",
  },
  {
    title: "Using different kV or MVA bases for the three sequence networks",
    body: "In per-unit sequence analysis, all three sequence networks \u2014 positive, negative, and zero \u2014 must use exactly the same MVA base and the same kV base on each voltage level. The zero-sequence impedance Z\u2080 is often 3\u00d7 (or more) different from Z\u2081 in actual ohms; this reflects a real physical difference in impedances, NOT a base difference. Always confirm that the sequence impedances you are using (from a relay coordination package or textbook) are expressed on the same base as your system MVA and kV.",
  },
];

const CommonMistakes = () => (
  <SectionWrapper number="5" title="Common Mistakes" id="common-mistakes">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      Symmetrical components errors fall into three categories: algebra errors (a vs a², missing 1/3),
      physics errors (zero-sequence grounding assumptions), and per-unit network errors. Identifying
      which category applies guides the fix directly.
    </p>
    <Accordion type="single" collapsible className="space-y-2" data-testid="mistakes-accordion">
      {MISTAKES.map((m, i) => (
        <AccordionItem
          key={i}
          value={`mistake-${i}`}
          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 bg-white dark:bg-slate-900"
        >
          <AccordionTrigger className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:no-underline hover:text-teal-700 dark:hover:text-teal-400 py-3">
            <span className="flex items-center gap-2.5 text-left">
              <span className="text-red-500 text-xs font-bold w-5 flex-shrink-0">#{i + 1}</span>
              {m.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pb-3">{m.body}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </SectionWrapper>
);

// ─── Section 6: Worked Example ────────────────────────────────────────────────
const STEPS = [
  {
    label: 'State the problem',
    text: 'Given: Va = 1\u22200\u00b0 pu, Vb = 0, Vc = 0. This represents phases B and C collapsed to ground \u2014 the extreme SLG fault pattern. Find the positive-, negative-, and zero-sequence components V\u2081, V\u2082, V\u2080 using the forward transform. Then verify using the inverse transform.',
    formula: null,
    result: null,
  },
  {
    label: 'Record the a-operator values',
    text: 'Before calculating, note the exact complex values. These must be precise for hand calculations.',
    formula: "a = 1\\angle 120^\\circ \\approx {-}0.5 + j0.866 \\qquad a^2 = 1\\angle 240^\\circ \\approx {-}0.5 - j0.866",
    result: null,
  },
  {
    label: 'Calculate V\u2080 \u2014 Zero-sequence component',
    text: 'Zero sequence is the arithmetic average of all three phase phasors. Since Vb = Vc = 0, only Va contributes.',
    formula: "V_0 = \\tfrac{1}{3}(V_a + V_b + V_c) = \\tfrac{1}{3}(1 + 0 + 0) = \\frac{1}{3}\\,\\text{pu}\\;\\angle 0^\\circ",
    result: 'V\u2080 = 0.3333 pu \u22200\u00b0',
  },
  {
    label: 'Calculate V\u2081 \u2014 Positive-sequence component',
    text: 'The a-operator pre-rotates Vb and a\u00b2 pre-rotates Vc before summing. Since Vb = Vc = 0, all operator terms vanish.',
    formula: "V_1 = \\tfrac{1}{3}(V_a + a\\cdot V_b + a^2\\cdot V_c) = \\tfrac{1}{3}(1 + a\\cdot 0 + a^2\\cdot 0) = \\frac{1}{3}\\,\\text{pu}\\;\\angle 0^\\circ",
    result: 'V\u2081 = 0.3333 pu \u22200\u00b0',
  },
  {
    label: 'Calculate V\u2082 \u2014 Negative-sequence component',
    text: 'Same simplification: a\u00b2 multiplies Vb and a multiplies Vc, but both terms are zero again.',
    formula: "V_2 = \\tfrac{1}{3}(V_a + a^2\\cdot V_b + a\\cdot V_c) = \\tfrac{1}{3}(1 + a^2\\cdot 0 + a\\cdot 0) = \\frac{1}{3}\\,\\text{pu}\\;\\angle 0^\\circ",
    result: 'V\u2082 = 0.3333 pu \u22200\u00b0',
  },
  {
    label: 'Identify the fault type from the result',
    text: 'Equal sequence magnitudes are the mathematical fingerprint of any SLG fault. Whenever you see this pattern in a relay event record, the fault involved exactly one phase going to ground.',
    formula: "|V_0| = |V_1| = |V_2| = \\frac{1}{3}\\,\\text{pu} \\quad\\Rightarrow\\quad \\text{SLG fault signature}",
    result: 'Equal magnitudes confirmed: single-line-to-ground (SLG) fault',
  },
  {
    label: 'Verify using the inverse transform',
    text: 'Reconstruct Va, Vb, Vc from V\u2080, V\u2081, V\u2082 to confirm the original phasors. The key identity 1 + a + a\u00b2 = 0 makes Vb and Vc vanish.',
    formula: "V_a = \\tfrac{1}{3}+\\tfrac{1}{3}+\\tfrac{1}{3} = 1\\angle 0^\\circ\\;\\checkmark \\quad V_b = \\tfrac{1}{3}(1 + a^2 + a) = \\tfrac{1}{3}(0) = 0\\;\\checkmark \\quad V_c = \\tfrac{1}{3}(1 + a + a^2) = 0\\;\\checkmark",
    result: 'Inverse transform verified \u2713 \u2014 original phasors recovered exactly',
  },
];

const WorkedExample = () => (
  <SectionWrapper number="6" title="Step-by-Step Worked Example" id="worked-example">
    <div className="mb-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest mb-2">
        Problem Statement
      </p>
      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
        A fault collapses phases B and C to ground while phase A remains at rated voltage (extreme SLG
        pattern). Phase voltages:{' '}
        <strong className="font-mono text-slate-900 dark:text-white">Va = 1\u22200\u00b0 pu</strong>,{' '}
        <strong className="font-mono text-slate-900 dark:text-white">Vb = 0</strong>,{' '}
        <strong className="font-mono text-slate-900 dark:text-white">Vc = 0</strong>.
        Decompose into sequence components using the forward transform and verify with the inverse.
      </p>
    </div>
    <div className="space-y-3" data-testid="worked-example-steps">
      {STEPS.map((step, i) => (
        <div
          key={i}
          className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900"
        >
          <div className="flex items-start gap-3">
            <div className="section-badge flex-shrink-0 text-xs">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-sm mb-2 font-heading">
                {step.label}
              </p>
              {step.text && (
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">
                  {step.text}
                </p>
              )}
              {step.formula && (
                <div className="overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3 my-2">
                  <BlockMath math={step.formula} />
                </div>
              )}
              {step.result && (
                <div className="inline-flex items-center gap-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-md px-3 py-1.5 mt-1">
                  <ChevronRight className="h-3 w-3 text-teal-600" />
                  <span className="text-xs font-mono font-semibold text-teal-800 dark:text-teal-300">
                    {step.result}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// ─── Section 7: Interactive Calculators ───────────────────────────────────────
const CalculatorsSection = () => (
  <SectionWrapper number="7" title="Interactive Calculators" id="calculators" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      Three calculators in learning order: build intuition with the a-operator first, then decompose
      real phasor sets, then explore the full live visualization with imbalance metrics.
    </p>
    <SequenceLegend />
    <div className="mt-5">
      <Tabs defaultValue="a-operator" data-testid="calculators-tabs">
        <TabsList className="flex flex-wrap gap-1 h-auto mb-6 bg-slate-100 dark:bg-slate-800/60 p-1 rounded-lg">
          <TabsTrigger
            value="a-operator"
            data-testid="tab-a-operator"
            className="text-xs font-medium px-3 py-2 rounded-md flex-shrink-0"
          >
            1. a-Operator Explorer
          </TabsTrigger>
          <TabsTrigger
            value="decomposer"
            data-testid="tab-decomposer"
            className="text-xs font-medium px-3 py-2 rounded-md flex-shrink-0"
          >
            2. Sequence Decomposer
          </TabsTrigger>
          <TabsTrigger
            value="phasor-viz"
            data-testid="tab-phasor-viz"
            className="text-xs font-medium px-3 py-2 rounded-md flex-shrink-0"
          >
            3. Live Phasor Visualizer
          </TabsTrigger>
        </TabsList>

        <TabsContent value="a-operator">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 bg-white dark:bg-slate-900">
            <div className="mb-4">
              <p className="font-semibold text-slate-900 dark:text-white text-sm font-heading mb-1">
                a-Operator Explorer
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                See exactly what multiplication by a, a², and a³ does to any input phasor V.
                Rotate the input and observe the consistent 120° spacing on the unit circle.
              </p>
            </div>
            <AOperatorExplorer />
          </div>
        </TabsContent>

        <TabsContent value="decomposer">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 bg-white dark:bg-slate-900">
            <div className="mb-4">
              <p className="font-semibold text-slate-900 dark:text-white text-sm font-heading mb-1">
                Sequence Decomposer Calculator
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Enter any three-phase voltage set (magnitude and angle) and instantly compute V\u2081,
                V\u2082, V\u2080. Includes a six-phasor diagram showing both phase phasors (solid lines)
                and their sequence equivalents (dashed).
              </p>
            </div>
            <SequenceDecomposerCalc />
          </div>
        </TabsContent>

        <TabsContent value="phasor-viz">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 bg-white dark:bg-slate-900">
            <div className="mb-4">
              <p className="font-semibold text-slate-900 dark:text-white text-sm font-heading mb-1">
                Live Sequence Phasor Visualizer
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Adjust all six phase parameters using sliders. The phasor diagram and sequence outputs
                update continuously. The imbalance ratio V\u2082/V\u2081 checks grid compliance
                (&lt;2% per IEC 61000-3-13).
              </p>
            </div>
            <SequencePhasorViz />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  </SectionWrapper>
);

// ─── Section 8: Self-Check Quiz ───────────────────────────────────────────────
const QUESTIONS = [
  {
    q: 'The a-operator is defined as:',
    options: [
      { k: 'a', label: 'a = 1\u222060\u00b0' },
      { k: 'b', label: 'a = 1\u222090\u00b0' },
      { k: 'c', label: 'a = 1\u2220120\u00b0' },
      { k: 'd', label: 'a = 1\u2220240\u00b0' },
    ],
    correct: 'c',
    explanation: 'a = 1\u2220120\u00b0 = e^{j2\u03c0/3} \u2248 \u22120.5 + j0.866. It is a unit rotation operator (|a|=1) that shifts any phasor exactly 120\u00b0 counterclockwise. Note: 1\u2220240\u00b0 is a\u00b2, not a. Confusing a and a\u00b2 is the most common algebra error in symmetrical components.',
  },
  {
    q: 'For a perfectly balanced three-phase set (Va=1\u22200\u00b0, Vb=1\u2220\u2212120\u00b0, Vc=1\u2220120\u00b0), which sequence component is non-zero?',
    options: [
      { k: 'a', label: 'V\u2080 only (zero sequence)' },
      { k: 'b', label: 'V\u2081 only (positive sequence)' },
      { k: 'c', label: 'V\u2081 and V\u2082 only' },
      { k: 'd', label: 'All three: V\u2080, V\u2081, V\u2082' },
    ],
    correct: 'b',
    explanation: 'A balanced ABC set is a pure positive-sequence set by definition. Applying the forward transform gives V\u2081 = 1\u22200\u00b0 pu, V\u2080 = V\u2082 = 0. This is why positive-sequence analysis alone works for normal steady-state balanced operation \u2014 only one sequence network is needed.',
  },
  {
    q: 'The mathematical identity 1 + a + a\u00b2 equals:',
    options: [
      { k: 'a', label: '3' },
      { k: 'b', label: '\u221a3' },
      { k: 'c', label: 'j\u221a3' },
      { k: 'd', label: '0' },
    ],
    correct: 'd',
    explanation: '1 + a + a\u00b2 = 0. Verification: 1 + (\u22120.5+j0.866) + (\u22120.5\u2212j0.866) = (1\u22120.5\u22120.5) + j(0.866\u22120.866) = 0 \u2713. This identity explains why V\u2080 = V\u2082 = 0 for any balanced set, and appears in the inverse transform verification step.',
  },
  {
    q: 'Zero-sequence current cannot flow in line conductors connected to:',
    options: [
      { k: 'a', label: 'A solidly grounded wye winding' },
      { k: 'b', label: 'An earth return path' },
      { k: 'c', label: 'A neutral conductor in a 4-wire system' },
      { k: 'd', label: 'A delta-connected winding' },
    ],
    correct: 'd',
    explanation: 'Zero-sequence current cannot flow in the external line conductors connected to a delta winding \u2014 there is no neutral or ground return path in those conductors. (Zero-sequence current does circulate internally inside the delta loop, but does not exit into the external network.) This makes the delta side an open circuit in the zero-sequence network.',
  },
  {
    q: 'For Va = 1\u22200\u00b0 pu, Vb = 0, Vc = 0, what are the sequence component magnitudes?',
    options: [
      { k: 'a', label: 'V\u2081 = 1 pu, V\u2080 = V\u2082 = 0 pu' },
      { k: 'b', label: 'V\u2080 = V\u2081 = V\u2082 = 1/3 pu' },
      { k: 'c', label: 'V\u2081 = 2/3 pu, V\u2080 = V\u2082 = 1/6 pu' },
      { k: 'd', label: 'V\u2080 = 1 pu, V\u2081 = V\u2082 = 0 pu' },
    ],
    correct: 'b',
    explanation: 'V\u2080 = V\u2081 = V\u2082 = (1/3)(1+0+0) = 1/3 pu. All three rows of the forward transform receive only Va = 1; the Vb = Vc = 0 terms vanish regardless of the a/a\u00b2 multipliers. The equal three-way split is the mathematical fingerprint of any SLG fault.',
  },
];

const SelfCheckQuiz = () => {
  const [selected, setSelected] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = submitted ? QUESTIONS.filter((q, i) => selected[i] === q.correct).length : 0;
  const reset = () => { setSelected({}); setSubmitted(false); };

  return (
    <SectionWrapper number="8" title="Self-Check Quiz" id="quiz">
      <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
        Five questions covering the a-operator definition, sequence types, the 1+a+a²=0 identity,
        zero-sequence grounding behavior, and the SLG fault decomposition result.
      </p>
      <div className="space-y-6" data-testid="quiz-section">
        {QUESTIONS.map((q, qi) => (
          <div
            key={qi}
            data-testid={`quiz-question-${qi}`}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5"
          >
            <p className="font-medium text-slate-900 dark:text-white text-sm mb-3">
              <span className="text-teal-700 dark:text-teal-400 font-mono mr-2">Q{qi + 1}.</span>
              {q.q}
            </p>
            <div className="space-y-2">
              {q.options.map(opt => {
                const isSel     = selected[qi] === opt.k;
                const isCorrect = opt.k === q.correct;
                let cls = 'border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2 w-full text-left ';
                if (!submitted) {
                  cls += isSel
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'
                    : 'border-slate-200 dark:border-slate-700 hover:border-teal-400 text-slate-700 dark:text-slate-300';
                } else {
                  if (isCorrect)      cls += 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100';
                  else if (isSel)     cls += 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100';
                  else                cls += 'border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-600 opacity-50';
                }
                return (
                  <button
                    key={opt.k}
                    data-testid={`quiz-option-${qi}-${opt.k}`}
                    className={cls}
                    onClick={() => !submitted && setSelected(prev => ({ ...prev, [qi]: opt.k }))}
                    disabled={submitted}
                  >
                    {submitted && isCorrect  && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    {submitted && isSel && !isCorrect && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                    <span className="font-mono text-xs font-bold mr-1">{opt.k.toUpperCase()}.</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {submitted && (
              <div className="mt-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3">
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  <span className="font-semibold text-teal-700 dark:text-teal-400">Explanation: </span>
                  {q.explanation}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-6">
        {!submitted ? (
          <button
            data-testid="quiz-submit"
            onClick={() => Object.keys(selected).length > 0 && setSubmitted(true)}
            disabled={Object.keys(selected).length === 0}
            className="px-5 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors text-sm"
          >
            Submit Answers ({Object.keys(selected).length}/{QUESTIONS.length} answered)
          </button>
        ) : (
          <>
            <div
              data-testid="quiz-score"
              className={`px-4 py-2 rounded-md text-sm font-semibold ${
                score === QUESTIONS.length
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                  : score >= 3
                  ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                  : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}
            >
              Score: {score}/{QUESTIONS.length}
              {score === QUESTIONS.length && ' \u2014 Perfect!'}
              {score >= 3 && score < QUESTIONS.length && ' \u2014 Good work'}
              {score < 3 && ' \u2014 Review the formulas above and try again'}
            </div>
            <button
              data-testid="quiz-reset"
              onClick={reset}
              className="text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 underline transition-colors"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </SectionWrapper>
  );
};

// ─── Section 9: Connected Topics & Coming Next ────────────────────────────────
const ConnectedTopics = () => (
  <SectionWrapper number="9" title="Connected Topics" id="connected-topics" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      Symmetrical components is the mathematical bridge between balanced-system analysis (M01–M03) and
      unbalanced fault analysis (M06). The sequence decomposition developed here becomes the input to
      the sequence network Thevenin models built in M05.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">
          Prerequisites
        </p>
        <div className="space-y-2">
          {[
            {
              to: '/per-unit',
              label: 'M03 Per-Unit Analysis',
              desc: 'All sequence calculations use per-unit. Z\u2081, Z\u2082, Z\u2080 must be on the same MVA/kV base.',
            },
            {
              to: '/transformers',
              label: 'M02 Transformer Systems',
              desc: 'Delta zero-sequence blocking and transformer connection type determine Z\u2080 in sequence networks.',
            },
          ].map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-white dark:bg-slate-900"
            >
              <ArrowLeft className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">
          Next Steps
        </p>
        <div className="space-y-2">
          {[
            {
              to: '/sequence-networks',
              label: 'M05 Sequence Networks',
              desc: 'Build Thevenin Z\u2081, Z\u2082, Z\u2080 models \u2014 the circuit side of fault analysis.',
            },
            {
              to: '/fault-analysis',
              label: 'M06 Fault Analysis',
              desc: 'Connect the three sequence networks to compute SLG, LL, DLG fault currents.',
            },
          ].map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-3 border border-teal-100 dark:border-teal-900/40 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-teal-50/50 dark:bg-teal-900/10"
            >
              <ArrowRight className="h-4 w-4 text-teal-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>

    {/* Coming Next card */}
    <div className="bg-gradient-to-br from-indigo-50 to-teal-50 dark:from-indigo-950/40 dark:to-teal-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-mono font-semibold text-indigo-700 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded">
          M05
        </span>
        <p className="text-sm font-semibold text-slate-900 dark:text-white font-heading">
          Coming Next: Sequence Networks
        </p>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
        In this module you learned to <em>decompose</em> any unbalanced three-phase condition into three
        balanced sequence sets using the a-operator. Module 05 answers the next question:{' '}
        <strong className="text-slate-800 dark:text-slate-200">
          what is the Thevenin equivalent impedance of the power system at the fault bus, in each
          sequence domain?
        </strong>
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
        M05 builds the positive-, negative-, and zero-sequence Thevenin networks — incorporating
        generators, transformers (with their connection-dependent zero-sequence models from M02), and
        line impedances. Once you have Z\u2081, Z\u2082, Z\u2080 at the fault bus, the M04 decomposition
        framework connects the three networks at the fault point and yields the complete fault current
        solution for any fault type.
      </p>
      <Link
        to="/sequence-networks"
        data-testid="coming-next-m05-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
      >
        Go to M05 Sequence Networks
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </SectionWrapper>
);

// ─── Main Module Page ─────────────────────────────────────────────────────────
const SymmetricalComponentsModule = () => (
  <div data-testid="symmetrical-components-module">
    {/* Module Header */}
    <div className="relative bg-gradient-to-br from-[#0B172A] via-[#1a1040] to-[#0d2a26] text-white px-4 sm:px-6 lg:px-8 py-12 overflow-hidden">
      {/* Decorative sequence color bars at the bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex h-1">
        <div className="flex-1" style={{ background: '#0d9488' }} />
        <div className="flex-1" style={{ background: '#ea580c' }} />
        <div className="flex-1" style={{ background: '#7c3aed' }} />
      </div>
      <div className="relative max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-5 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Curriculum Map
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono font-semibold text-purple-400 bg-purple-400/10 border border-purple-400/20 px-2 py-1 rounded">
            M04
          </span>
          <span className="text-xs font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">LIVE</span>
          <span className="text-xs text-slate-400 uppercase tracking-widest">
            Prerequisite: M03 Per-Unit Analysis
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3 leading-tight">
          Symmetrical Components
        </h1>
        <p className="text-slate-300 text-lg mb-4">
          Fortescue's Theorem — Positive, Negative, and Zero Sequence
        </p>
        <p className="text-slate-400 text-sm max-w-2xl mb-4">
          Decompose any unbalanced three-phase fault into three balanced sequence sets using the
          a-operator. The mathematical foundation for all ground-fault analysis, relay coordination,
          and voltage-unbalance studies.
        </p>
        <div className="flex flex-wrap gap-2">
          <span
            className="text-xs px-2.5 py-1 rounded-full border font-mono"
            style={{ background: '#0d948815', borderColor: '#0d948840', color: '#5eead4' }}
          >
            {'Positive \u2192 Teal'}
          </span>
          <span
            className="text-xs px-2.5 py-1 rounded-full border font-mono"
            style={{ background: '#ea580c15', borderColor: '#ea580c40', color: '#fb923c' }}
          >
            {'Negative \u2192 Orange'}
          </span>
          <span
            className="text-xs px-2.5 py-1 rounded-full border font-mono"
            style={{ background: '#7c3aed15', borderColor: '#7c3aed40', color: '#a78bfa' }}
          >
            {'Zero \u2192 Purple'}
          </span>
        </div>
      </div>
    </div>

    {/* Section Nav */}
    <div className="sticky top-14 z-20 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <div className="flex gap-0 py-0" style={{ scrollbarWidth: 'none' }}>
          {[
            { n: 1, label: 'Why',         href: '#why-it-matters' },
            { n: 2, label: 'Animator',    href: '#concept-visual' },
            { n: 3, label: 'Theory',      href: '#core-theory' },
            { n: 4, label: 'Formulas',    href: '#formulas' },
            { n: 5, label: 'Mistakes',    href: '#common-mistakes' },
            { n: 6, label: 'Example',     href: '#worked-example' },
            { n: 7, label: 'Calculators', href: '#calculators' },
            { n: 8, label: 'Quiz',        href: '#quiz' },
            { n: 9, label: 'Next',        href: '#connected-topics' },
          ].map(({ n, label, href }) => (
            <a
              key={n}
              href={href}
              className="px-3 py-3 text-xs font-medium text-slate-500 dark:text-slate-500 hover:text-teal-700 dark:hover:text-teal-400 whitespace-nowrap border-b-2 border-transparent hover:border-teal-500 transition-colors flex-shrink-0"
            >
              <span className="text-teal-600 dark:text-teal-500 mr-1 font-mono">{n}.</span>{label}
            </a>
          ))}
        </div>
      </div>
    </div>

    {/* All 9 Sections */}
    <WhyItMatters />
    <ConceptAnimator />
    <CoreTheory />
    <FormulaSheet />
    <CommonMistakes />
    <WorkedExample />
    <CalculatorsSection />
    <SelfCheckQuiz />
    <ConnectedTopics />

    {/* Bottom navigation */}
    <div className="border-t border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <Link
          to="/per-unit"
          data-testid="prev-module-btn"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          M03 Per-Unit Analysis
        </Link>
        <Link
          to="/sequence-networks"
          data-testid="next-module-btn"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
        >
          M05 Sequence Networks
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default SymmetricalComponentsModule;
